import type { SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isNil } from 'es-toolkit';

export type UsePollOptions = {
  interval?: number;
  maxTime?: number;
  maxCount?: number;
  enabled?: boolean;
  immediate?: boolean;
  onError?: (error: unknown) => void;
  onPollingExpired?: () => void;
};

export type UsePollResult = {
  pollCount: number;
  isPolling: boolean;
  startPolling: () => void;
  stopPolling: () => void;
  pollingExpired: boolean;
  reset: () => void;
};

export function usePoll(callback: () => unknown, options: UsePollOptions = {}): UsePollResult {
  const { interval = 1000, maxTime, maxCount, enabled = true, immediate = true, onError, onPollingExpired } = options;

  const callbackRef = useRef(callback);
  const onErrorRef = useRef(onError);
  const onPollingExpiredRef = useRef(onPollingExpired);
  const pollCountRef = useRef(0);
  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingRef = useRef(false);

  const [pollCountState, setPollCountState] = useState(0);
  const [isPollingState, setIsPollingState] = useState(false);
  const [pollingExpired, setPollingExpired] = useState(false);

  const setPollCount = useCallback((set: SetStateAction<number>) => {
    setPollCountState((prev) => (typeof set === 'function' ? set(prev) : set));
    pollCountRef.current = typeof set === 'function' ? set(pollCountRef.current) : set;
  }, []);

  const setIsPolling = useCallback((set: SetStateAction<boolean>) => {
    setIsPollingState((prev) => (typeof set === 'function' ? set(prev) : set));
    isPollingRef.current = typeof set === 'function' ? set(isPollingRef.current) : set;
  }, []);

  const clearTimers = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    clearTimers();
    setIsPolling(false);
  }, [clearTimers, setIsPolling]);

  const handlePollingExpired = useCallback(() => {
    setPollingExpired(true);
    onPollingExpiredRef.current?.();
    stopPolling();
  }, [stopPolling]);

  const shouldStopForMaxCount = useCallback(() => {
    return !isNil(maxCount) && pollCountRef.current >= maxCount;
  }, [maxCount]);

  const executePoll = useCallback(async () => {
    if (shouldStopForMaxCount()) {
      handlePollingExpired();
      return;
    }

    try {
      await callbackRef.current();
    } catch (error) {
      onErrorRef.current?.(error);
    }

    setPollCount((prev) => prev + 1);

    if (intervalIdRef.current && shouldStopForMaxCount()) {
      handlePollingExpired();
    }
  }, [handlePollingExpired, shouldStopForMaxCount, setPollCount]);

  const startPolling = useCallback(() => {
    if (isPollingRef.current || !enabled) {
      return;
    }

    setPollCount(0);
    setIsPolling(true);
    setPollingExpired(false);

    if (!isNil(maxTime)) {
      timeoutIdRef.current = setTimeout(handlePollingExpired, maxTime);
    }

    if (immediate) {
      executePoll();
    }

    intervalIdRef.current = setInterval(executePoll, interval);
  }, [setPollCount, setIsPolling, executePoll, interval, enabled, immediate, maxTime, handlePollingExpired]);

  const reset = useCallback(() => {
    clearTimers();
    setPollCount(0);
    setIsPolling(false);
  }, [clearTimers, setPollCount, setIsPolling]);

  useEffect(() => {
    if (enabled && !isPollingRef.current) {
      startPolling();
    } else if (!enabled && isPollingRef.current) {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [enabled, startPolling, stopPolling]);

  useEffect(() => {
    callbackRef.current = callback;
    onErrorRef.current = onError;
    onPollingExpiredRef.current = onPollingExpired;
  }, [callback, onError, onPollingExpired]);

  return {
    pollCount: pollCountState,
    isPolling: isPollingState,
    startPolling,
    stopPolling,
    pollingExpired,
    reset,
  };
}
